import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, Typography, Box, Button, IconButton, Grid } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';
import { setmedia } from '../../../redux/project/actions';
import { Plus, X, Play } from 'lucide-react';
import { UserProjectSearchDialogue } from '../../UserProjectSearchDialogue/UserProjectSearchDialogue';
import { IProject } from '../../../types/global/helper';

interface ICompanyVideosEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const CompanyVideosEdit: React.FC<ICompanyVideosEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        company_videos: company?.company_videos || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [isProjectSearchOpen, setIsProjectSearchOpen] = useState(false);

    useEffect(() => {
        if (company) {
            const newData = {
                company_videos: company.company_videos || [],
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleSave = () => {
        if (company?._id) {
            const payload: any = {
                id: company._id,
                body: {
                    company_videos: formData.company_videos.map(vid => {
                        return {
                            _id: vid._id,
                        };
                    }),
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const handleAddVideos = (selectedProjects: IProject[]) => {
        setFormData(prev => ({
            ...prev,
            company_videos: [...prev.company_videos, ...selectedProjects],
        }));
        setIsProjectSearchOpen(false);
    };

    const handleRemoveVideo = (videoId: string) => {
        setFormData(prev => ({
            ...prev,
            company_videos: prev.company_videos.filter(video => video._id !== videoId),
        }));
    };

    const handlePlayVideo = (video: IProject) => {
        dispatch(
            setmedia({
                media: video,
                mediaType: 'video',
            }),
        );
    };

    return (
        <Card
            sx={{
                backgroundColor: theme[themeStyle].secondaryBg,
                border: `1px solid ${theme[themeStyle].tag_white_light}`,
                borderRadius: 2,
            }}>
            <CardHeader
                title="Company Videos"
                action={
                    isEditing && (
                        <Button
                            startIcon={<Plus size={16} />}
                            onClick={() => setIsProjectSearchOpen(true)}
                            sx={{
                                color: theme[themeStyle].highlight,
                                borderColor: theme[themeStyle].highlight,
                                '&:hover': {
                                    borderColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].primary_orange,
                                },
                            }}
                            variant="outlined">
                            Add Videos
                        </Button>
                    )
                }
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.BOLD_HEADER },
                }}
            />
            <CardContent>
                {formData.company_videos.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme[themeStyle].tag_white,
                            opacity: 0.7,
                        }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            No videos added yet. Click "Add Videos" to get started.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {formData.company_videos.map((video, index) => (
                            <Grid item xs={12} sm={6} md={4} key={video._id}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: 200,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: theme[themeStyle].deep_grey,
                                        border: `1px solid ${theme[themeStyle].tag_white_light}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}>
                                    {/* Remove Button */}
                                    {isEditing && (
                                        <IconButton
                                            onClick={() => handleRemoveVideo(video._id)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: theme[themeStyle].tag_white,
                                                width: 32,
                                                height: 32,
                                                zIndex: 3,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.9)',
                                                },
                                            }}>
                                            <X size={16} />
                                        </IconButton>
                                    )}

                                    {/* Video Thumbnail */}
                                    <Box
                                        onClick={() => handlePlayVideo(video)}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundImage: `url(${video.thumbnail || video.feature_image})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                        }}>
                                        {/* Play Button Overlay */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'background-color 0.2s ease-in-out',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                },
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        backgroundColor: 'white',
                                                        transform: 'scale(1.1)',
                                                    },
                                                }}>
                                                <Play
                                                    size={20}
                                                    color={theme[themeStyle].pure_black}
                                                    fill={theme[themeStyle].pure_black}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Video Title Overlay */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                                            p: 2,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                            }}>
                                            {video.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </CardContent>

            {/* Project Search Dialog */}
            <UserProjectSearchDialogue
                open={isProjectSearchOpen}
                onClose={() => setIsProjectSearchOpen(false)}
                type="video"
                maxSelection={10}
                onSubmit={handleAddVideos}
            />
        </Card>
    );
};

export { CompanyVideosEdit };
